/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useState } from 'react';
import type { DealProposal } from '@/services/requests/housien-services/DealsService';

/** The modals the deal detail screen can open, one at a time. */
export type DealDialog =
  | 'none'
  | 'proposal'
  | 'contact'
  | 'lose'
  | 'property'
  | 'edit'
  | 'visit'
  | 'newAttendance'
  | 'win';

/**
 * Single-slot modal state for the detail screen.
 *
 * One piece of state rather than a boolean per dialog: the actions are
 * mutually exclusive (you cannot be recording a proposal and losing the
 * deal at once), and six independent booleans made it possible to open
 * two at once.
 *
 * @returns The open dialog and the open/close callbacks.
 */
export function useDealDetailDialogs() {
  const [dialog, setDialog] = useState<DealDialog>('none');
  // The offer a counter is answering. It rides with the dialog state
  // rather than beside it, so closing can never leave a stale offer
  // pre-filling the next fresh proposal.
  const [counterTo, setCounterTo] = useState<DealProposal | undefined>();

  const open = useCallback((next: DealDialog) => {
    setCounterTo(undefined);
    setDialog(next);
  }, []);

  const openCounter = useCallback((proposal: DealProposal) => {
    setCounterTo(proposal);
    setDialog('proposal');
  }, []);

  const close = useCallback(() => {
    setCounterTo(undefined);
    setDialog('none');
  }, []);

  return { dialog, counterTo, open, openCounter, close };
}
